import { useState, type FormEvent } from "react";

import { supabase } from "../lib/supabase";

/**
 * Where a password-reset link lands.
 *
 * The link has already signed them in by the time this renders — that is what
 * the reset token buys — so there is nothing to verify here and no token to
 * carry around. All that remains is choosing the password.
 *
 * On success `updateUser` raises USER_UPDATED, which clears the recovery flag
 * in useAccess and lets the dashboard through. This component does not route
 * anywhere itself; there is nothing for it to get wrong.
 *
 * No minimum length is printed. The real rule lives in the project's auth
 * settings, and a screen that quotes a number the server does not enforce is
 * worse than one that lets the server answer.
 */
export function NewPassword() {
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();

    if (password !== again) {
      setError("Those two passwords are not the same.");
      return;
    }

    setBusy(true);
    setError(null);
    const { error } = await supabase.auth.updateUser({ password });
    setBusy(false);

    // Leaked-password protection and the strength rules both answer here, and
    // they explain themselves better than anything this screen could invent.
    if (error) setError(error.message);
  }

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <a className="logo" href="https://shiftygroup.com">
          shifty<span>.</span>
        </a>

        <h1>Choose a new password</h1>
        <p className="sub">
          Pick something long that you do not use anywhere else. You will be signed in as
          soon as it is saved.
        </p>

        {error && <div className="notice error">{error}</div>}

        <form onSubmit={onSubmit}>
          <label htmlFor="password">New password</label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <label htmlFor="again">New password again</label>
          <input
            id="again"
            type="password"
            autoComplete="new-password"
            required
            value={again}
            onChange={(e) => setAgain(e.target.value)}
          />

          <button className="btn" type="submit" disabled={busy}>
            {busy ? "One moment…" : "Save new password"}
          </button>
        </form>

        {/* A way out for somebody who opened the link by mistake, or who has
            decided they would rather keep the password they have. */}
        <button className="btn quiet" type="button" onClick={() => supabase.auth.signOut()}>
          Cancel and sign out
        </button>

        <p className="auth-foot">
          Trouble? Write to <a href="mailto:info@shiftygroup.com">info@shiftygroup.com</a>.
        </p>
      </div>
    </div>
  );
}
